import { type FC, useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { useConfig } from "@jti/app-context/useConfig";
import { dispatchXSellingDialogEvent, getXSellingPopupDataForCart, XSellingDialogEvents } from "@jti/commerce/features";
import { hasOngoingSubscriptionError, sortByStandaloneGifts } from "@jti/commerce/shared";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectCartPending } from "@jti/store/selectors/selectCartPending";
import { selectPrices } from "@jti/store/selectors/selectPrices";
import { selectProductsInCart } from "@jti/store/selectors/selectProductsInCart";
import { selectQtyPending } from "@jti/store/selectors/selectQtyPending";
import { cartMiniSlice } from "@jti/store/slices/cartMiniSlice";
import {
  fetchCartPrices,
  fetchCleanCartItems,
  removeItem,
  removeItems,
  updateItemQuantity,
} from "@jti/store/slices/cartSlice";
import { updateTradeInOrderAttributes } from "@jti/store/slices/LEGACY__checkoutSlice";
import { useSetAdditionalAttributesMutation } from "@jti/store/slices/magentoApiSlice";
import { isRegularGift } from "@jti/store/utils";
import type { TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { useBodyScrollLock } from "@jti/ui";

import { ThemeVariantProvider } from "@checkout/contexts/CartMiniContext";
import { buildCommerceBaseData } from "@checkout/hooks/gtm/trackingHelpers";
import useGtmQuantityChange from "@checkout/hooks/gtm/useGtmQuantityChange";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import { CartMiniContentContainer } from "../CartMiniContentContainer";

type RootContainerProps = {
  onNavigate?: (url: string) => void;
  shopButtonLabel?: string;
};

const navigateWithBrowser = (url: string) => {
  window.location.href = url;
};

export const RootContainer: FC<RootContainerProps> = ({ onNavigate = navigateWithBrowser, shopButtonLabel }) => {
  const dispatch = useDispatch();
  const { CART_CHECKOUT_PAGE_URL, isXSellingPopupEnabled, showPricesWithTaxExcluded = false } = useConfig();
  const cart = useSelector((state) => state.cart);
  const cartId = cart.id;
  const [setAdditionalAttributes] = useSetAdditionalAttributesMutation();
  const sortedProductsInCart = [
    ...useSelector((state) => selectProductsInCart(state, false, showPricesWithTaxExcluded)),
  ].sort(sortByStandaloneGifts);
  const prices = useSelector((state) => selectPrices(state, showPricesWithTaxExcluded));

  const itemsPending = useSelector(selectCartPending);
  const qtyPending = useSelector(selectQtyPending);
  const { opened } = useSelector((state) => state.cartMini);
  const grandTotal = useSelector((state) => state.cart.prices.data?.grandTotal.value);
  const shipping = useSelector((state) => state.cart.prices.data?.shipping?.value);
  const { is_order_limit_enabled: isLimitEnabled, order_limit_value: maxOrderLimit } = useSelector(
    (state) => state.cart.storeConfig
  );

  const isMobileOrTablet = useMediaQuery({ maxWidth: 767 });
  useGtmQuantityChange();

  const [limitMessageStatus, setLimitMessageStatus] = useState(false);
  const [isUserClickedToOpenCart, setIsUserClickedToOpenCart] = useState(false);

  const { toggleScroll } = useBodyScrollLock(opened);

  useEffect(() => {
    if (isMobileOrTablet) {
      toggleScroll(opened);
    }
  }, [opened, isMobileOrTablet, toggleScroll]);

  useEffect(() => {
    if (cartId) {
      dispatch(fetchCleanCartItems(cartId));
    }
  }, [dispatch, cartId]);

  const hasOngoingSubscriptionErrorInCart = hasOngoingSubscriptionError(sortedProductsInCart);

  useEffect(() => {
    if (opened && cartId && hasOngoingSubscriptionErrorInCart) {
      dispatch(fetchCleanCartItems(cartId));
    }
  }, [dispatch, opened, cartId, hasOngoingSubscriptionErrorInCart]);

  useEffect(() => {
    setLimitMessageStatus((grandTotal || 0) - (shipping || 0) > maxOrderLimit && isLimitEnabled);
  }, [grandTotal]);

  // track mini cart open state
  useEffect(() => {
    if (opened && isUserClickedToOpenCart) {
      const data: TrackingEventsAdditionalData[TrackingEvents.TrackingCartOpen] = buildCommerceBaseData(cart);
      dispatchTrackingEvent(TrackingEvents.TrackingCartOpen, data);
    }
  }, [opened, isUserClickedToOpenCart]);

  // handlers
  const handleQuantityChange = useCallback(
    async (itemId: string, quantity: number) => {
      if (!cartId) return;

      const item = sortedProductsInCart.find(({ id }) => id === itemId);
      if (!item) return;

      await dispatch(updateItemQuantity({ cartId, itemId, quantity }));
      dispatch(fetchCleanCartItems(cartId));
      dispatch(fetchCartPrices(cartId));

      if (item.tradeIn) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [dispatch, cartId, sortedProductsInCart, setAdditionalAttributes]
  );

  const handleRemoveItem = useCallback(
    async (itemId: string) => {
      if (!cartId) return;

      const item = sortedProductsInCart.find(({ id }) => id === itemId);

      if (item) {
        await dispatch(removeItem({ cartId, item }));
      }

      dispatch(fetchCleanCartItems(cartId));

      if (item?.tradeIn) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [dispatch, cartId, sortedProductsInCart, setAdditionalAttributes]
  );

  const handleRemoveItems = useCallback(
    async (itemIds: string[]) => {
      if (!cartId) return;

      await dispatch(removeItems({ cartId, itemIds }));
      dispatch(fetchCleanCartItems(cartId));

      const tradeInItem = sortedProductsInCart.some((item) => item.tradeIn);
      if (tradeInItem) {
        dispatch(updateTradeInOrderAttributes(null));
        setAdditionalAttributes({ cartId, additionalAttributes: [] });
      }
    },
    [dispatch, cartId, sortedProductsInCart, setAdditionalAttributes]
  );

  const handleOpen = useCallback(() => {
    dispatch(cartMiniSlice.actions.open());
    setIsUserClickedToOpenCart(true);
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(cartMiniSlice.actions.close());
    setIsUserClickedToOpenCart(false);
  }, [dispatch]);

  const handleScroll = useCallback(() => {
    if (opened) {
      dispatch(cartMiniSlice.actions.close());
    }
  }, [dispatch, opened]);

  const handleCheckoutClick = useCallback(async () => {
    handleClose();

    if (!isXSellingPopupEnabled) {
      onNavigate(CART_CHECKOUT_PAGE_URL);
      return;
    }

    const { shouldDisplay, skus } = await getXSellingPopupDataForCart(cartId);

    if (shouldDisplay) {
      dispatchXSellingDialogEvent(XSellingDialogEvents.open, { skus });
    } else {
      onNavigate(CART_CHECKOUT_PAGE_URL);
    }
  }, [handleClose, cartId, CART_CHECKOUT_PAGE_URL, isXSellingPopupEnabled, onNavigate]);

  useEffect(() => {
    if (!opened) return;

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll, opened]);

  const subtotal = prices?.subtotal;
  const subtotalPrice = subtotal && { currency: subtotal.currency, value: subtotal.value };

  return (
    <ThemeVariantProvider>
      <CartMiniContentContainer
        subtotal={subtotalPrice}
        items={sortedProductsInCart}
        opened={opened}
        onOpen={handleOpen}
        onClose={handleClose}
        warningStatus={limitMessageStatus}
        isLimitEnabled={isLimitEnabled}
        itemsCount={getCartItemsCount(sortedProductsInCart.filter((item) => !isRegularGift(item)))}
        itemsPending={itemsPending}
        qtyPending={qtyPending}
        onItemQuantityChange={handleQuantityChange}
        onRemoveItems={handleRemoveItems}
        onRemoveItemClick={handleRemoveItem}
        onCheckoutClick={handleCheckoutClick}
        isOnCartPage={window.location.href.includes(CART_CHECKOUT_PAGE_URL)}
        shopButtonLabel={shopButtonLabel}
      />
    </ThemeVariantProvider>
  );
};
